import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, of, BehaviorSubject, throwError } from 'rxjs';
import { map, catchError, finalize } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';

import { TokenService } from './token.service';
import { UserService } from './user.service';
import { ApiResponse } from '../models';
import { AppConfigService } from './app-config.service';

interface RefreshResponse {
  StatusCode?: number;
  Error?: string | null;
  Message?: string | null;
  Status: boolean;
  Data?: {
    AccessToken: string;
    RefreshToken?: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _isRefreshing = false;
  private _tokenSubject = new BehaviorSubject<string | null>(null);
  private refreshUrl = `${this.appConfig.masterBaseUrl?.replace(/\/+$/, '')}/User/RefreshThirdPartyToken`;

  constructor(
    private jwtHelperService: JwtHelperService,
    private tokenService: TokenService,
    private userService: UserService,
    private http: HttpClient,
    private appConfig: AppConfigService
  ) { }

  get userId(): number | null {
    const token = this.getDecodedToken();
    return token ? Number(token.nameid) : localStorage.getItem('userId') ? Number(localStorage.getItem('userId')) : localStorage.getItem('employeeId') ? Number(localStorage.getItem('employeeId')) : null;
  }

  get getUserRole() {
    return JSON.parse(localStorage.getItem('trole') || 'null');
  }

  getDecodedToken() {
    try {
      return this.jwtHelperService.decodeToken(this.tokenService.token);
    } catch (error) {
      console.error('[AuthService] Token decode error:', error);
      return null;
    }
  }

  getLoggedUserDetails(): Observable<ApiResponse | null> {
    if (this.userId) {
      return this.userService.getUserDetails(this.userId);
    }
    return of(null);
  }

  refreshToken(refreshToken: string): Observable<{ accessToken: string; refreshToken?: string }> {
    if (this._isRefreshing) {
      return this._tokenSubject.pipe(
        map(token => ({ accessToken: token || '', refreshToken }))
      );
    }

    this._isRefreshing = true;
    this._tokenSubject.next(null);

    const body = { RefreshToken: refreshToken };

    return this.http.post<RefreshResponse>(this.refreshUrl, body).pipe(
      map((res) => {
        if (!res?.Status || !res?.Data?.AccessToken) {
          throw new Error(res?.Message || res?.Error || 'Invalid refresh response');
        }

        this.tokenService.ssoAccessTokenSet(res.Data.AccessToken);
        if (res.Data.RefreshToken) {
          this.tokenService.ssoRefreshTokenSet(res.Data.RefreshToken);
        }

        this._tokenSubject.next(res.Data.AccessToken);

        return {
          accessToken: res.Data.AccessToken,
          refreshToken: res.Data.RefreshToken,
        };
      }),
      catchError((err) => {
        console.error('[AuthService.refreshToken] Error:', err);
        this.tokenService.signOut();
        return throwError(() => err);
      }),
      finalize(() => {
        this._isRefreshing = false;
      })
    );
  }

  getRefreshState() {
    return {
      isRefreshing: this._isRefreshing,
      tokenSubject: this._tokenSubject.asObservable(),
    };
  }
}
